import { Download, Plus, CreditCard, Filter } from 'lucide-react';
import { Avatar, Button, DataTable, PageHeader, type Column } from '../components/ui';
import { MockDataBanner } from '../components/MockDataBanner';
import { MOCK_PAYMENTS } from '../data/mock';
import type { Payment, PaymentStatus } from '../types';

const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  paid: 'Paid',
  failed: 'Failed',
  refunded: 'Refunded',
  pending: 'Pending',
};

const PAYMENT_STATUS_COLOR: Record<PaymentStatus, { text: string; dot: string }> = {
  paid: { text: 'text-sage', dot: 'bg-sage' },
  failed: { text: 'text-rust', dot: 'bg-rust' },
  refunded: { text: 'text-ink-4', dot: 'bg-ink-5' },
  pending: { text: 'text-spark', dot: 'bg-spark' },
};

function PaymentStatusPill({ status }: { status: PaymentStatus }) {
  const cfg = PAYMENT_STATUS_COLOR[status];
  return (
    <div className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium ${cfg.text}`}>
      <div className={`dot ${cfg.dot}`} />
      {PAYMENT_STATUS_LABEL[status]}
    </div>
  );
}

export function Payments() {
  // TODO: useQuery(GET_PAYMENTS)

  const columns: Column<Payment>[] = [
    {
      key: 'date',
      header: 'Date',
      colSpan: 2,
      render: (p) => (
        <div>
          <div className="text-[13px] font-medium num">{p.date}</div>
          <div className={`text-[11px] ${p.status === 'failed' ? 'text-rust' : 'text-ink-4'}`}>
            {p.dateRelative}
          </div>
        </div>
      ),
    },
    {
      key: 'client',
      header: 'Client',
      colSpan: 3,
      render: (p) => (
        <div className="flex items-center gap-2 min-w-0">
          <Avatar
            initials={p.client.initials}
            className={p.client.initials === 'JP' ? 'bg-tide text-paper-hi' : ''}
          />
          <span className="text-[13px] font-medium truncate">{p.client.name}</span>
        </div>
      ),
    },
    {
      key: 'session',
      header: 'Session',
      colSpan: 3,
      hideBelow: 'md',
      render: (p) => <div className="text-[12.5px] text-ink-3 truncate">{p.sessionRef}</div>,
    },
    {
      key: 'method',
      header: 'Method',
      colSpan: 2,
      hideBelow: 'lg',
      render: (p) => (
        <div className="text-[12.5px] text-ink-3 flex items-center gap-1.5 truncate">
          <CreditCard size={12} strokeWidth={1.8} className="flex-shrink-0" />
          {p.method}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      colSpan: 1,
      hideBelow: 'md',
      render: (p) => <PaymentStatusPill status={p.status} />,
    },
    {
      key: 'amount',
      header: 'Amount',
      colSpan: 1,
      align: 'right',
      render: (p) => {
        const isRefunded = p.status === 'refunded';
        const isFailed = p.status === 'failed';
        return (
          <div
            className={`font-display text-[14px] num ${
              isFailed ? 'text-rust' : isRefunded ? 'line-through text-ink-4' : ''
            }`}
          >
            ${p.amount.toLocaleString()}
          </div>
        );
      },
    },
  ];

  const filterTabs: { label: string; count?: number; active?: boolean; dot?: string }[] = [
    { label: 'All', count: 152, active: true },
    { label: 'Successful' },
    { label: 'Failed', dot: 'bg-rust' },
    { label: 'Refunded' },
  ];

  const tableFooter = (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <span>Showing 10 of 152</span>
      <div className="flex items-center gap-3">
        <span className="font-display num text-[13px]">Net this view · $6,950</span>
        <button className="text-tide font-medium hover:underline">View all →</button>
      </div>
    </div>
  );

  const rowHighlight = (p: Payment) => {
    if (p.status === 'failed') return 'bg-rust-s/30';
    if (p.status === 'refunded') return 'opacity-70';
    return undefined;
  };

  return (
    <div>
      <MockDataBanner />
      {/* Page header */}
      <PageHeader
        actions={
          <>
            <Button variant="secondary" icon={<Download size={13} strokeWidth={1.8} />}>
              Export CSV
            </Button>
            <Button variant="primary" icon={<Plus size={13} strokeWidth={2.2} />}>
              Record payment
            </Button>
          </>
        }
      />

      <div className="px-4 md:px-7 pb-6">
        {/* KPI cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <div className="bg-paper-hi rounded-lg p-5 shadow-card">
            <div className="text-[11.5px] text-ink-4 font-medium mb-3">April · gross</div>
            <div className="font-display text-[28px] font-medium num">$42,180</div>
            <div className="mt-1 text-[11.5px]">
              <span className="text-sage font-medium">↑ 18.2%</span>{' '}
              <span className="text-ink-4">vs March</span>
            </div>
          </div>
          <div className="bg-paper-hi rounded-lg p-5 shadow-card">
            <div className="text-[11.5px] text-ink-4 font-medium mb-3">Successful</div>
            <div className="font-display text-[28px] font-medium num">
              142
              <span className="text-ink-4 text-[18px] font-light ml-1">charges</span>
            </div>
            <div className="mt-1 text-[11.5px]">
              <span className="text-sage font-medium">97.9%</span>{' '}
              <span className="text-ink-4">success rate</span>
            </div>
          </div>
          <div className="bg-paper-hi rounded-lg p-5 shadow-card relative">
            <div className="absolute top-3 right-3">
              <span className="dot bg-rust pulse-soft" />
            </div>
            <div className="text-[11.5px] text-ink-4 font-medium mb-3">
              Failed · awaiting retry
            </div>
            <div className="font-display text-[28px] font-medium num text-rust">
              2<span className="text-ink-4 text-[18px] font-light ml-2">$750</span>
            </div>
            <div className="mt-1 text-[11.5px] text-ink-3">Robin W. · Elena R.</div>
          </div>
          <div className="bg-paper-hi rounded-lg p-5 shadow-card">
            <div className="text-[11.5px] text-ink-4 font-medium mb-3">Refunded · April</div>
            <div className="font-display text-[28px] font-medium num">$450</div>
            <div className="mt-1 text-[11.5px] text-ink-4">1 refund · rescheduled session</div>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-1 bg-paper-hi rounded-md p-1 hair border">
            {filterTabs.map((t) => (
              <button
                key={t.label}
                className={`px-3 py-1.5 text-[12.5px] rounded flex items-center gap-1.5 ${
                  t.active
                    ? 'bg-ink text-paper-hi font-medium'
                    : 'text-ink-3 hover:bg-paper-2'
                }`}
              >
                {t.dot && <span className={`dot ${t.dot}`} />}
                {t.label}
                {t.count !== undefined && (
                  <span className={`text-[10.5px] num ${t.active ? 'opacity-60' : 'text-ink-5'}`}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" icon={<Filter size={12} strokeWidth={2} />}>
              Last 30 days
            </Button>
            <Button variant="secondary" size="sm">
              All providers
            </Button>
          </div>
        </div>
      </div>

      {/* Ledger */}
      <div className="px-4 md:px-7 pb-20">
        <DataTable<Payment>
          columns={columns}
          data={MOCK_PAYMENTS}
          footer={tableFooter}
          rowHighlight={rowHighlight}
        />
      </div>
    </div>
  );
}
